import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const AlertesPanel = ({ departementId = null, limit = 5 }) => {
  const [alertes, setAlertes] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, non_lues, critiques

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 60000); // Refresh toutes les minutes
    return () => clearInterval(interval);
  }, [departementId, filter]);

  const loadData = async () => {
    try {
      const filters = {};
      if (departementId) filters.departement_id = departementId;
      if (filter === 'non_lues') filters.non_lues = true;
      if (filter === 'critiques') filters.priorite = 'critique';

      const [alertesData, statsData] = await Promise.all([
        api.alertes.getAll(filters),
        api.alertes.statistiques()
      ]);

      setAlertes((alertesData.data || []).slice(0, limit));
      setStats(statsData);
    } catch (error) {
      console.error('Erreur chargement alertes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarquerLue = async (id) => {
    try {
      await api.alertes.marquerLue(id);
      loadData();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleResoudre = async (id) => {
    try {
      await api.alertes.resoudre(id);
      loadData();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const getPriorityColor = (priorite) => {
    const colors = {
      info: '#2196f3',
      normale: '#4caf50',
      importante: '#ff9800',
      urgente: '#ff5722',
      critique: '#f44336'
    };
    return colors[priorite] || '#666';
  };

  const getTypeIcon = (type) => {
    const icons = {
      absence: '🚫',
      retard: '⏰',
      performance: '📉',
      budget: '💰',
      echeance: '📅',
      contrat: '📄',
      paiement: '💳',
      autre: '📌'
    };
    return icons[type] || '📌';
  };

  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
        ⏳ Chargement des alertes...
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* En-tête avec stats */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 16 
      }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
          ⚠️ Alertes
        </h3>
        <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
          <span style={{ 
            background: '#ffebee', 
            color: '#c62828', 
            padding: '4px 10px', 
            borderRadius: 12,
            fontWeight: 600 
          }}>
            {stats.critiques || 0} critiques
          </span>
          <span style={{ 
            background: '#e3f2fd', 
            color: '#1976d2', 
            padding: '4px 10px', 
            borderRadius: 12,
            fontWeight: 600 
          }}>
            {stats.non_lues || 0} non lues
          </span>
        </div>
      </div>

      {/* Filtres */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button
          onClick={() => setFilter('all')}
          style={{
            padding: '6px 12px',
            fontSize: 12,
            border: 'none',
            borderRadius: 6,
            background: filter === 'all' ? '#2196f3' : '#f5f5f5',
            color: filter === 'all' ? 'white' : '#666',
            cursor: 'pointer',
            fontWeight: filter === 'all' ? 600 : 400
          }}
        >
          Toutes
        </button>
        <button
          onClick={() => setFilter('non_lues')}
          style={{
            padding: '6px 12px',
            fontSize: 12,
            border: 'none',
            borderRadius: 6,
            background: filter === 'non_lues' ? '#2196f3' : '#f5f5f5',
            color: filter === 'non_lues' ? 'white' : '#666',
            cursor: 'pointer',
            fontWeight: filter === 'non_lues' ? 600 : 400
          }}
        >
          Non lues
        </button>
        <button
          onClick={() => setFilter('critiques')}
          style={{
            padding: '6px 12px',
            fontSize: 12,
            border: 'none',
            borderRadius: 6,
            background: filter === 'critiques' ? '#f44336' : '#f5f5f5',
            color: filter === 'critiques' ? 'white' : '#666',
            cursor: 'pointer',
            fontWeight: filter === 'critiques' ? 600 : 400
          }}
        >
          Critiques
        </button>
      </div>

      {/* Liste des alertes */}
      {alertes.length === 0 ? (
        <div style={{ 
          padding: 30, 
          textAlign: 'center', 
          background: '#f9f9f9', 
          borderRadius: 10,
          color: '#66bb6a'
        }}>
          ✅ Aucune alerte active
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {alertes.map(alerte => (
            <div
              key={alerte.id}
              style={{
                background: alerte.lu_le ? '#fafafa' : 'white',
                border: `1px solid ${getPriorityColor(alerte.priorite)}20`,
                borderLeft: `4px solid ${getPriorityColor(alerte.priorite)}`,
                borderRadius: 8,
                padding: 12,
                opacity: alerte.lu_le ? 0.7 : 1
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{getTypeIcon(alerte.type)}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontWeight: 600, 
                    fontSize: 13,
                    marginBottom: 4,
                    color: '#333'
                  }}>
                    {alerte.titre}
                  </div>
                  {alerte.description && (
                    <div style={{ 
                      fontSize: 12, 
                      color: '#666',
                      marginBottom: 8,
                      lineHeight: 1.4
                    }}>
                      {alerte.description}
                    </div>
                  )}
                  
                  {/* Actions requises */}
                  {alerte.actions_requises && alerte.actions_requises.length > 0 && (
                    <div style={{ marginTop: 8 }}>
                      {alerte.actions_requises.map((action, idx) => (
                        <div key={idx} style={{ 
                          fontSize: 11, 
                          color: '#888',
                          marginBottom: 3
                        }}>
                          {action.fait ? '✅' : '⬜'} {action.action}
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ 
                    display: 'flex', 
                    gap: 8, 
                    marginTop: 10,
                    fontSize: 11
                  }}>
                    <span style={{ 
                      background: getPriorityColor(alerte.priorite) + '20',
                      color: getPriorityColor(alerte.priorite),
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontWeight: 600
                    }}>
                      {alerte.priorite}
                    </span>
                    <span style={{ color: '#999' }}>
                      {new Date(alerte.created_at).toLocaleDateString('fr-FR')}
                    </span>
                    {alerte.date_echeance && (
                      <span style={{ color: '#ff9800' }}>
                        ⏰ {new Date(alerte.date_echeance).toLocaleDateString('fr-FR')}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div style={{ 
                display: 'flex', 
                gap: 6, 
                marginTop: 10,
                paddingTop: 10,
                borderTop: '1px solid #f0f0f0'
              }}>
                {!alerte.lu_le && (
                  <button
                    onClick={() => handleMarquerLue(alerte.id)}
                    style={{
                      padding: '4px 12px',
                      fontSize: 11,
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      background: 'white',
                      color: '#666',
                      cursor: 'pointer'
                    }}
                  >
                    👁️ Marquer lu
                  </button>
                )}
                {alerte.statut === 'active' && (
                  <button
                    onClick={() => handleResoudre(alerte.id)}
                    style={{
                      padding: '4px 12px',
                      fontSize: 11,
                      border: 'none',
                      borderRadius: 4,
                      background: '#4caf50',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    ✓ Résoudre
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AlertesPanel;
